<template>
    <div @click="navigateToSurvey"
        class="relative bg-black rounded-lg shadow-md text-white p-4 flex flex-col min-w-[280px] min-h-[280px] cursor-pointer hover:shadow-lg transition-shadow duration-300 overflow-hidden">
        <div class="absolute inset-0 bg-cover bg-center"
            :style="{ backgroundImage: `url(${backgroundImage})` }"></div>
        <div class="absolute inset-0 bg-gradient-overlay"></div>
        <h3 class="relative z-10 text-xl font-black font-lato mb-2 flex items-center">
            <i :class="getSurveyTypeIcon(survey)" class="text-primary mr-2"></i>
            {{ getSurveyTypeTitle(survey) }}
        </h3>
        
        <!-- Status Badge - Top Right -->
        <div class="absolute top-2 right-2 z-20">
            <div class="flex items-center bg-neutral-800 bg-opacity-90 px-2 py-1 rounded-full">
                <i :class="getStatusIcon(getSurveyStatus(survey))" :style="getStatusIconColor(getSurveyStatus(survey))" class="mr-1 text-xs"></i>
                <span class="text-xs font-black font-lato">{{ getStatusText(getSurveyStatus(survey)).charAt(0).toUpperCase() + getStatusText(getSurveyStatus(survey)).slice(1) }}</span>
            </div>
        </div>
        
        <!-- Date - Bottom Left -->
        <div v-if="getSurveyDate(survey)" class="absolute bottom-2 left-2 z-20">
            <div class="bg-neutral-800 bg-opacity-90 px-2 py-1 rounded text-xs text-neutral-300">
                {{ formatDateShort(getSurveyDate(survey)) }}
            </div>
        </div>
        
        <!-- Stats Grid - Lower -->
        <div v-if="stats" class="absolute inset-x-4 bottom-12 z-20">
            <div class="grid grid-cols-2 gap-2">
                <!-- Coral Cover -->
                <div class="flex flex-col bg-neutral-800 bg-opacity-100 p-2 rounded-lg">
                    <span class="text-lg font-black font-lato text-white">{{ coralCover }}%</span>
                    <span class="text-xs text-neutral-400">Coral Cover</span>
                </div>
                
                <!-- Health Ratio -->
                <div class="flex flex-col bg-neutral-800 bg-opacity-100 p-2 rounded-lg">
                    <span class="text-lg font-black font-lato text-white">{{ healthRatio }}%</span>
                    <span class="text-xs text-neutral-400">Health</span>
                </div>
                
                <!-- Algae Cover -->
                <div class="flex flex-col bg-neutral-800 bg-opacity-100 p-2 rounded-lg">
                    <span class="text-lg font-black font-lato text-white">{{ algaeCover.toFixed(1) }}%</span>
                    <span class="text-xs text-neutral-400">Algae</span>
                </div>
                
                <!-- Bleaching -->
                <div class="flex flex-col bg-neutral-800 bg-opacity-100 p-2 rounded-lg">
                    <span class="text-lg font-black font-lato text-white">{{ bleachingPercent }}%</span>
                    <span class="text-xs text-neutral-400">Bleaching</span>
                </div>
            </div>
        </div>
        
        <!-- Storage path tooltip -->
        <div v-if="survey.storagePath" class="absolute bottom-2 right-2 z-20">
            <i class="mdi mdi-folder text-neutral-500 text-xs" :title="survey.storagePath"></i>
        </div>
    </div>
</template>

<script>
export default {
    name: 'SurveyCard',
    props: {
        branchId: {
            type: String,
            required: true
        },
        outplantSiteId: {
            type: String,
            required: true
        },
        survey: {
            type: Object,
            required: true
        },
        stats: {
            type: Object,
            default: null
        }
    },
    computed: {
        backgroundImage() {
            try {
                return require('@/assets/images/spatial_survey_cover.png');
            } catch (e) {
                console.warn('Spatial survey cover image not found. Using default.');
                return require('@/assets/images/background.png');
            }
        },
        coralCover() {
            if (!this.stats) return null;
            const totalSurfaceArea = parseFloat(this.stats.total_surface_area) || 0;
            const liveCoralArea = parseFloat(this.stats.total_live_coral_area) || 0;
            return totalSurfaceArea > 0 ? ((liveCoralArea / totalSurfaceArea) * 100).toFixed(1) : 0;
        },
        healthRatio() {
            if (!this.stats) return null;
            const liveArea = parseFloat(this.stats.total_live_coral_area) || 0;
            const deadArea = this.calculateDeadCoralArea();
            const totalCoralArea = liveArea + deadArea;
            return totalCoralArea > 0 ? ((liveArea / totalCoralArea) * 100).toFixed(1) : 0;
        },
        algaeCover() {
            if (!this.stats) return null;
            return parseFloat(this.stats.class_algae_covered_substrate_area_percent) || 0;
        },
        bleachingPercent() {
            if (!this.stats) return null;
            const bleachedTypes = [
                'massive_meandering_bleached',
                'other_coral_bleached',
                'branching_bleached',
                'meandering_bleached'
            ];

            const totalBleachedArea = bleachedTypes.reduce((total, type) => {
                return total + (parseFloat(this.stats[`class_${type}_area`]) || 0);
            }, 0);

            const totalSurfaceArea = parseFloat(this.stats.total_surface_area) || 0;
            return totalSurfaceArea > 0 ? ((totalBleachedArea / totalSurfaceArea) * 100).toFixed(1) : 0;
        }
    },
    methods: {
        navigateToSurvey() {
            // Navigate to the survey detail page
            this.$router.push({
                name: 'Survey',
                params: {
                    branchId: this.branchId,
                    outplantSiteId: this.outplantSiteId,
                    surveyId: this.survey.id
                }
            });
        },
        formatDateShort(date) {
            if (!date) return '';
            
            // Handle different date formats
            let dateObj;
            if (typeof date === 'string') {
                dateObj = new Date(date);
            } else if (date.toDate && typeof date.toDate === 'function') {
                // Firestore timestamp
                dateObj = date.toDate();
            } else if (date instanceof Date) {
                dateObj = date;
            } else {
                return '';
            }
            
            return dateObj.toLocaleDateString('en-US', {
                month: 'short',
                day: 'numeric',
                year: 'numeric'
            });
        },
        formatDate(date) {
            if (!date) return '';
            
            // Handle different date formats
            let dateObj;
            if (typeof date === 'string') {
                dateObj = new Date(date);
            } else if (date.toDate && typeof date.toDate === 'function') {
                // Firestore timestamp
                dateObj = date.toDate();
            } else if (date instanceof Date) {
                dateObj = date;
            } else {
                return '';
            }
            
            return dateObj.toLocaleDateString('en-US', {
                month: 'short',
                day: 'numeric',
                year: 'numeric',
                hour: '2-digit',
                minute: '2-digit'
            });
        },
        getStatusColorClass(status) {
            if (!status) return 'bg-gray-400';
            
            switch (status.toLowerCase()) {
                case 'completed':
                case 'done':
                    return 'bg-green-500';
                case 'pending':
                    return 'bg-yellow-500';
                case 'processing':
                case 'uploading':
                    return 'bg-blue-500';
                case 'failed':
                case 'error':
                    return 'bg-red-500';
                default:
                    return 'bg-gray-400';
            }
        },
        getStatusBadgeClass(status) {
            if (!status) return 'bg-gray-600 text-gray-200';
            
            switch (status.toLowerCase()) {
                case 'completed':
                case 'done':
                    return 'bg-green-600 text-green-100';
                case 'pending':
                    return 'bg-yellow-600 text-yellow-100';
                case 'processing':
                case 'uploading':
                    return 'bg-blue-600 text-blue-100';
                case 'failed':
                case 'error':
                    return 'bg-red-600 text-red-100';
                default:
                    return 'bg-gray-600 text-gray-200';
            }
        },
        getStatusIcon(status) {
            if (!status) return 'mdi mdi-checkbox-marked-circle-outline';
            
            switch (status.toLowerCase()) {
                case 'completed':
                case 'done':
                    return 'mdi mdi-checkbox-marked-circle-outline';
                case 'pending':
                    return 'mdi mdi-clock-outline';
                case 'processing':
                case 'uploading':
                    return 'mdi mdi-loading mdi-spin';
                case 'failed':
                case 'error':
                    return 'mdi mdi-alert-circle-outline';
                default:
                    return 'mdi mdi-checkbox-marked-circle-outline';
            }
        },
        getStatusText(status) {
            if (!status) return 'completed';
            
            switch (status.toLowerCase()) {
                case 'pending':
                    return 'processing';
                case 'completed':
                case 'done':
                    return 'completed';
                case 'uploading':
                    return 'uploading';
                case 'processing':
                    return 'processing';
                case 'failed':
                case 'error':
                    return 'failed';
                default:
                    return status;
            }
        },
        getStatusIconColor(status) {
            if (!status) return { color: '#10b981' }; // Green for completed
            
            switch (status.toLowerCase()) {
                case 'completed':
                case 'done':
                    return { color: '#10b981' }; // Green
                case 'pending':
                    return { color: '#f59e0b' }; // Yellow
                case 'processing':
                case 'uploading':
                    return { color: '#3b82f6' }; // Blue
                case 'failed':
                case 'error':
                    return { color: '#ef4444' }; // Red
                default:
                    return { color: '#6b7280' }; // Gray
            }
        },
        getSurveyTypeTitle(survey) {
            // Check for eventType first, then fall back to type
            const eventType = survey.eventType || survey.eventData?.eventType;
            const surveyType = survey.type;
            
            if (eventType === 'coralSurvey') {
                return 'Coral';
            } else if (eventType === 'fishSurvey') {
                return 'Fish';
            } else if (surveyType) {
                return surveyType;
            } else {
                return 'Survey';
            }
        },
        getSurveyTypeIcon(survey) {
            // Check for eventType first, then fall back to type
            const eventType = survey.eventType || survey.eventData?.eventType;
            
            if (eventType === 'coralSurvey') {
                return 'mdi mdi-coral';
            } else if (eventType === 'fishSurvey') {
                return 'mdi mdi-fish';
            } else {
                return 'mdi mdi-cube';
            }
        },
        getSurveyDate(survey) {
            // Try different date fields in order of preference
            return survey.date || 
                   survey.eventData?.surveyData?.date || 
                   survey.metadata?.createdAt ||
                   null;
        },
        getSurveyStatus(survey) {
            // Try different status fields in order of preference
            return survey.status || 
                   survey.eventData?.surveyStatus || 
                   survey.eventData?.surveyData?.surveyStatus ||
                   'completed';
        },
        calculateDeadCoralArea() {
            if (!this.stats) return 0;
            const deadTypes = [
                'massive_meandering_dead',
                'massive_meandering_bleached',
                'other_coral_dead',
                'other_coral_bleached',
                'branching_dead',
                'branching_bleached',
                'meandering_dead',
                'meandering_bleached'
            ];

            return deadTypes.reduce((total, type) => {
                return total + (parseFloat(this.stats[`class_${type}_area`]) || 0);
            }, 0);
        }
    }
}
</script>

<style scoped>
.bg-gradient-overlay {
    background: linear-gradient(to bottom,
            rgba(0, 0, 0, 0.8) 0%,
            rgba(0, 0, 0, 0.4) 20%,
            rgba(0, 0, 0, 0) 30%,
            rgba(0, 0, 0, 0) 35%,
            rgba(0, 0, 0, 0.3) 50%,
            rgba(0, 0, 0, 0.7) 70%,
            rgba(0, 0, 0, 1) 100%);
}
</style> 